function getClosestPointOnSegment(p, a, b) {
  const ap = { x: p.x - a.x, y: p.y - a.y };
  const ab = { x: b.x - a.x, y: b.y - a.y };
  const len2 = ab.x * ab.x + ab.y * ab.y;
  if (len2 === 0) return { x: a.x, y: a.y };
  let t = (ap.x * ab.x + ap.y * ab.y) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * ab.x, y: a.y + t * ab.y };
  }
  function applyMagnet(smoothed, original, size, magnetPower) {
  const maxPullDist = size * magnetPower; // Magnet power defined as a fraction of size for the maximum pull distance
  const newContour = [];
  for (let p of smoothed) {
  let minDistSq = Infinity;
  let closest = null;
  for (let i = 0; i < original.length; i++) {
  const a = original[i];
  const b = original[(i + 1) % original.length];
  const c = getClosestPointOnSegment(p, a, b);
  const dSq = (p.x - c.x) ** 2 + (p.y - c.y) ** 2;
  if (dSq < minDistSq) {
  minDistSq = dSq;
  closest = c;
  }
  }
  const dist = Math.sqrt(minDistSq);
  if (dist === 0) {
  newContour.push(p);
  continue;
  }
  const pullFactor = Math.max(0, 1 - dist / maxPullDist); // Linear falloff: strong pull when close, weak when far
  const dx = closest.x - p.x;
  const dy = closest.y - p.y;
  newContour.push({
  x: p.x + pullFactor * dx,
  y: p.y + pullFactor * dy
  });
  }
  return newContour;
  }
  function createPillowShape(smoothedContour, centerX, centerY, size, magnetPower = 7) {
  if (smoothedContour.length < 3) return smoothedContour;
  // Step 1: Compute convex hull (the "tight string" wrapped around the shape)
  // Using Graham scan algorithm
  function crossProduct(o, a, b) {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  }
  function computeConvexHull(points) {
  if (points.length < 3) return points.map(p => ({ x: p.x, y: p.y }));
  // Find bottom-most point (or leftmost in case of tie)
  let bottom = 0;
  for (let i = 1; i < points.length; i++) {
  if (points[i].y < points[bottom].y ||
  (points[i].y === points[bottom].y && points[i].x < points[bottom].x)) {
  bottom = i;
  }
  }
  // Swap bottom point to first position
  const temp = points[0];
  points[0] = points[bottom];
  points[bottom] = temp;
  // Sort by polar angle with respect to bottom point
  const pivot = points[0];
  const sorted = points.slice(1).map(p => ({
  x: p.x,
  y: p.y,
  angle: Math.atan2(p.y - pivot.y, p.x - pivot.x),
  dist: Math.hypot(p.x - pivot.x, p.y - pivot.y)
  }));
  sorted.sort((a, b) => {
  if (Math.abs(a.angle - b.angle) < 1e-9) {
  return a.dist - b.dist;
  }
  return a.angle - b.angle;
  });
  // Build hull using Graham scan
  const hull = [
  { x: pivot.x, y: pivot.y },
  { x: sorted[0].x, y: sorted[0].y }
  ];
  for (let i = 1; i < sorted.length; i++) {
  const point = { x: sorted[i].x, y: sorted[i].y };
  // Remove points that create clockwise turns
  while (hull.length > 1 &&
  crossProduct(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) {
  hull.pop();
  }
  hull.push(point);
  }
  return hull;
  }
  // Compute convex hull of the input contour
  const hullPoints = computeConvexHull(smoothedContour.map(p => ({ x: p.x, y: p.y })));
  if (hullPoints.length < 3) return hullPoints;
  // Step 2: Resample hull to ensure consistent point density
  let hullLength = 0;
  for (let i = 0; i < hullPoints.length; i++) {
  const next = hullPoints[(i + 1) % hullPoints.length];
  hullLength += Math.hypot(next.x - hullPoints[i].x, next.y - hullPoints[i].y);
  }
  const desiredSpacing = size / 200;
  const pointCount = Math.max(128, Math.min(512, Math.round(hullLength / desiredSpacing)));
  const resampled = resamplePolygon(hullPoints, pointCount);
  // Step 3: Apply magnet effect to pull towards original contour
  megnitified= applyMagnet(resampled, smoothedContour, size, magnetPower);
  
  // Step 4: Expand edges outward like a balloon to prevent shadows
  // This creates the inflated balloon look with edges curving smoothly outward
  // Increased expansion to ensure edges go outward and don't create shadows
  const balloonExpandAmount = size * 0.025; // 2.5% expansion for visible balloon effect that prevents shadows
  const smoothingIterations = Math.max(20, Math.round(size / 10)); // Increased iterations for smoother edges
  
  // First pass: Expand and smooth to push edges outward (strong expansion)
  let smoothed = smoothContour(megnitified, Math.round(smoothingIterations * 0.5), balloonExpandAmount);
  
  // Second pass: Additional smoothing with moderate expansion to maintain balloon shape
  smoothed = smoothContour(smoothed, Math.round(smoothingIterations * 0.3), balloonExpandAmount * 0.6);
  
  // Third pass: More smoothing with light expansion to ensure edges stay outward
  smoothed = smoothContour(smoothed, Math.round(smoothingIterations * 0.15), balloonExpandAmount * 0.3);
  
  // Final pass: Pure smoothing without expansion for perfect smoothness
  smoothed = smoothContour(smoothed, Math.round(smoothingIterations * 0.05), 0);
  
  return smoothed;
  }